// DERIVADO DE: gestion-web-cliente
//   src/app/auxiliares/servicios/listado-eventos-genericos-vivo.ts
//     → `listar`, `reproducirSonido`
// CAMBIOS: pide sólo `_id` y el sonido, no el listado entero (acá no alimenta
//   ninguna tabla); con varios nuevos suena el más fuerte.

import { DestroyRef, Service, inject } from '@angular/core';
import type { IEventoGenerico } from 'modelos';

import { JerarquiaClientesStore } from '@compartido/clientes/jerarquia-clientes.store';
import { PermisosService } from '@core/permisos/permisos.service';
import { SesionStore } from '@core/sesion/sesion.store';
import { type Sonido, SonidosService } from '@core/ui/sonidos.service';

import { alcanceEventos, ramaHorarios, tiposEventoVisibles } from './alcance-eventos';
import { EventosHttp } from './entidades.http';

/** La misma cadencia del vivo de IRIX (`INTERVALO_POLLING_SEGUNDOS`). */
const CADENCIA_SEG = 10;

/** El tope del listado vivo (`TOPE_VIVO`): más allá, IRIX tampoco los ve. */
const TOPE = 1000;

/**
 * El aviso sonoro de eventos nuevos.
 *
 * Es el de IRIX: cada diez segundos se mira el listado vivo —los eventos de
 * alumbrado abiertos, dentro del horario de atención del cliente— y, si aparece
 * uno que en la vuelta anterior no estaba, suena el sonido de su categoría
 * (`valores.sonido`): Campana, Sirena o nada si es Silencio. Sin sonido
 * configurado, Campana.
 *
 * "Nuevo" es **nuevo en el vivo**, no recién creado: un evento que vuelve de la
 * espera también suena, porque vuelve a necesitar a alguien.
 *
 * **Corre en toda la aplicación**, no sólo en la vista de eventos, y también con
 * la pestaña en segundo plano: es justamente cuando el operador no está mirando
 * que el sonido sirve. Para que el navegador no espacie el temporizador, con el
 * operador inactivo suena el bucle mudo de IRIX
 * (`SonidosService.iniciarFondoEnInactividad`).
 *
 * Referencia, exactamente como IRIX:
 * - **Al entrar** no hay vuelta anterior: si ya hay eventos abiertos, suena una
 *   vez.
 * - **Al cambiar de contexto** (`reiniciar()` en IRIX) se descarta la
 *   referencia: vuelve a sonar si hay abiertos.
 * - **Silenciado se sigue consultando**, sólo que no suena: la referencia queda
 *   al día y al volver a activar el sonido no suena por lo que llegó mientras
 *   tanto.
 * - Cerrar sesión no la descarta (en IRIX el servicio es único y sobrevive al
 *   logout): el mismo contexto al volver a entrar sólo suena por lo nuevo.
 */
@Service()
export class AvisoEventosNuevosStore {
  private readonly http = inject(EventosHttp);
  private readonly permisos = inject(PermisosService);
  private readonly sesion = inject(SesionStore);
  private readonly jerarquia = inject(JerarquiaClientesStore);
  private readonly sonidos = inject(SonidosService);

  /** Los del vivo en la vuelta anterior, y con qué consulta se pidieron. */
  private anteriores?: { clave: string; ids: Set<string> };
  private consultando = false;

  constructor() {
    inject(SonidosService).iniciarFondoEnInactividad();
    const intervalo = setInterval(() => void this.revisar(), CADENCIA_SEG * 1000);
    inject(DestroyRef).onDestroy(() => clearInterval(intervalo));
    void this.revisar();
  }

  private async revisar(): Promise<void> {
    const idCliente = this.sesion.permisoActual()?.idCliente;
    const hijos = this.jerarquia.tieneHijos();
    if (!idCliente || hijos === undefined || !this.permisos.puedeVer('eventos')) return;
    // Una vuelta lenta no se pisa con la siguiente.
    if (this.consultando) return;
    this.consultando = true;

    const tipos = tiposEventoVisibles(this.permisos);
    const clave = JSON.stringify([idCliente, tipos, hijos]);
    try {
      const { datos } = await this.http.listar(
        {
          filter: JSON.stringify({
            ...alcanceEventos(tipos),
            requiereAtencion: true,
            $and: [ramaHorarios(idCliente)],
          }),
          select: '_id valores.sonido',
          sort: '-fechaCreacion',
          limit: TOPE,
          excludeTotalCount: true,
          includeChildren: hijos || undefined,
          // Bandera que el backend usa para elegir el índice parcial del vivo.
          vivo: 'true',
        } as Parameters<EventosHttp['listar']>[0],
        // Sondeo de fondo cada pocos segundos: un aviso de error por vuelta
        // fallida, en cualquier pantalla, no le sirve a nadie. Falla en silencio
        // y la próxima vuelta lo corrige.
        { silencioso: true },
      );

      const eventos = datos ?? [];
      // Sin referencia (al entrar) o de otro contexto: todo cuenta como nuevo.
      const previos = this.anteriores?.clave === clave ? this.anteriores.ids : new Set<string>();
      this.anteriores = { clave, ids: new Set(eventos.map((e) => e._id ?? '')) };
      if (this.sonidos.silenciado()) return;

      const nuevos = eventos.filter((e) => e._id && !previos.has(e._id));
      const sonido = sonidoDe(nuevos);
      if (sonido) this.sonidos.reproducir(sonido);
    } catch {
      // Una vuelta que falla no suena ni pierde la referencia: la próxima compara
      // contra lo último que sí llegó.
    } finally {
      this.consultando = false;
    }
  }
}

/**
 * Qué suena cuando llegan varios juntos: el más fuerte. Una sirena no se tapa
 * con una campana porque llegó en la misma vuelta.
 */
function sonidoDe(nuevos: readonly IEventoGenerico[]): Sonido | undefined {
  let sonido: Sonido | undefined;
  for (const evento of nuevos) {
    const pedido = (evento.valores as { sonido?: Sonido } | undefined)?.sonido ?? 'Campana';
    if (pedido === 'Sirena') return 'Sirena';
    if (pedido === 'Campana') sonido = 'Campana';
  }
  return sonido;
}
